<template lang="html">
  <section class="section">
    <HeaderSection :title="title" :tooltip="tooltip">
      <div class="flex is-justified-between mb-20 forms">

        <div v-if="is_search" class="flex grow1">
          <VqInput
            v-model="search"
            class="mr-20 w-full max-w-300"
            name="search"
            type="text"
            :label="$t('components.table.search')"
          />
        </div>

        <div v-if="hasExport || hasAdd" class="flex items-end is-justified-end grow1">
          <button
            v-if="hasExport && values.length"
            class="button is-xsmall is-success is-outline is-square export-btn"
            :title="$t('components.table.export')"
            @click="exportList">
            <i class="icon-csv"></i>
          </button>
          <button
            v-if="hasAdd && addPermission"
            class="button is-xsmall is-gamma"
            @click="addRow">
            <i class="icon-add"></i>
            {{addLabel}}
          </button>
        </div>

      </div>
    </HeaderSection>

    <table
      class="table"
      :class="{'has-actions': (hasRowAction)}">
      <thead>
        <tr>
          <th
            v-for="(col, index) in headers"
            :class="[col.classList]"
            class="nowrap"
            @click="sort(col.field)">
            <div v-if='col.field' class="pointer" :class="{'is-gamma': col.field === currentSortKey}" :title="col.title">
              <span v-if="isDescKey(col.field)" class="icon-caret-up nowrap"></span>
              <span v-else class="icon-caret-down nowrap"></span>
              {{ col.label }}
            </div>
            <div v-else>
              {{ col.label }}
            </div>
          </th>
          <th v-if="hasRowAction">{{ $t('components.table.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-if="!values.length"
          class="is-empty">
          <td>
            <div class="td-content is-left-aligned">{{ $t('components.table.empty') }}</div>
          </td>
        </tr>
        <tr
          v-for="(row, rowIndex) in computedValues">
          <td
            v-for="(col, index) in rows">
            <div class="td-label">
              {{ headers[index].label }}
            </div>
            <template v-if="getLink(col, row)">
              <div
                class="td-content">
                <a
                  :class="getClassList(col, row)"
                  :href="getLink(col, row).href"
                  :alt="getLink(col,row).alt"
                  :target="getLink(col,row).target"
                  :download="getLink(col,row).download"
                >
                  <i v-if="getIcon(col, row)" :class="getIcon(col, row)"></i>
                  <span :class="{'ml-5': getIcon(col, row) }">{{getValue(col,row)}}</span>
                </a>
              </div>
            </template>
            <template v-else-if="hasAction(col, row)">
              <div class="td-content">
                <button :class="getClassList(col, row)" v-on:click="getAction(col, row)">
                  <i v-if="getIcon(col, row)" :class="getIcon(col, row)"></i>
                  <span :class="{'ml-5': getIcon(col, row) }">{{getValue(col,row)}}</span>
                </button>
              </div>
            </template>
            <div v-else>
              <div
                class="td-content"
                :class="getClassList(col, row)"
                @mouseover="getIcon(col, row) == 'icon icon-star' ? $set(hover, rowIndex, true) : $set(hover, rowIndex, false)"
                @mouseleave="$set(hover, rowIndex, false)">

                <span v-if="getIcon(col, row) == 'icon icon-star' && hover[rowIndex] == true">{{getClassement(row.classement)}}{{getClassement(row.storage_profile)}}</span>
                <i
                  v-else-if="getIcon(col, row)"
                  :class="getIcon(col, row)"></i>
                <span>{{getValue(col, row)}}</span>
              </div>
            </div>
          </td>
        
          <td v-if="actionVMPermission() && (hasStateAction || showStateActionIndex==rowIndex)">
            <div class="mr-10 is-relative" v-click-outside="hideStateAction">
              <button
                v-if="hasStateAction"
                @click.stop="openActionDropDown(rowIndex)"
                class="button is-rounded is-square is-white is-xsmall">
                  <i class="icon-action is-info"></i>
              </button>
              <div v-if="showStateActionIndex==rowIndex" class="action-card" >
                <button v-if="!row.power_state" @click.stop="emitAction(row, 'powerOn')" type="button" class="button is-xxsmall is-gray-400 block">{{ $t('components.table.power') }}</button>
                <button @click.stop="emitAction(row, 'reset')" type="button" class="button is-xxsmall is-gray-400 block mt-10 ml-n">{{ $t('components.table.reset') }}</button>
                <button @click.stop="emitAction(row, 'restartGuestOs')" type="button" class="button is-xxsmall is-gray-400 block mt-10 ml-n">{{ $t('components.table.restart') }}</button>
              </div>
            </div>
          </td>

          <td v-if="hasEdit || hasDelete || seeMore" class="flex is-justified-end">
            <button
              v-if="hasEdit && updatePermission"
              @click="editRow(row)"
              class="button is-rounded is-square is-white is-xsmall mr-10">
                <i class="icon-pencil is-info"></i>
            </button>
            <button
              v-if="hasDelete && deletePermission"
              @click="deleteRow(row)"
              class="button is-rounded is-square is-white is-xsmall">
                <i class="icon-delete is-danger"></i>
            </button>
            <button
              v-if="seeMore"
              @click="openSeeMore(row)"
              class="button is-rounded is-square is-white is-xsmall">
                <i class="icon-eye is-info"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script lang="js">
import { mapGetters } from 'vuex';

import HeaderSection from '@/components/HeaderSection.vue';
import VqInput from '@/components/VqInput.vue';
import VqSelect from '@/components/VqSelect.vue';

import ClickOutside from 'vue-click-outside'

export default {
  name: 'Table',
  components: {
    HeaderSection,
    VqInput,
    VqSelect,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    values: {
      type: Array,
      required: true
    },
    searchValues: {
      type: Array,
      required: false,
    },
    addLabel: {
      type: String,
      required: false,
      default: '',
    },
    currentSortKey: {
      type: String,
      required: false,
      default: '',
    },
    is_search: {
      type: Boolean,
      required: false,
      default: true,
    },
    addPermission: {
      type: Boolean,
      required: false,
      default: true,
    },
    updatePermission: {
      type: Boolean,
      required: false,
      default: true,
    },
    deletePermission: {
      type: Boolean,
      required: false,
      default: true,
    },
    tooltip: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      showStateActionIndex: null,
      hover: [],
      // currentSortKey: '',
      currentSortDir: 'asc',
      search: '',
    };
  },

  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),
    hasEdit() {
      return (this.$listeners.hasOwnProperty('edit'));
    },
    hasRowAction() {
      return this.hasEdit || this.hasDelete || this.seeMore;
    },
    hasDelete() {
      return (this.$listeners.hasOwnProperty('delete'));
    },
    seeMore() {
      return (this.$listeners.hasOwnProperty('more'));
    },
    hasExport() {
      return (this.$listeners.hasOwnProperty('export'));
    },
    hasAdd() {
      return (this.$listeners.hasOwnProperty('add'));
    },
    hasStateAction() {
      return (this.$listeners.hasOwnProperty('action'));
    },
    computedValues() {
      // the use of slice clone the array
      // https://stackoverflow.com/questions/49869081/unexpected-side-effect-in-computed-property/49869125
      const clonedValues = this.values.slice();
      const sortedValues = this.sortValues(clonedValues);
      return this.filterSearch(sortedValues);
    },

  },
  methods: {
    sortValues(data) {
      return data.sort((a, b) => {
        let modifier = 1;
        let aVal; let bVal;
        //
        if (this.currentSortKey == 'article[0].from_email_format') {
          aVal = a.article[0].from_email_format;
          bVal = b.article[0].from_email_format;
        } else if (typeof c === 'string') {
          aVal = a[this.currentSortKey].toLowerCase();
          bVal = b[this.currentSortKey].toLowerCase();
        } else {
          aVal = a[this.currentSortKey];
          bVal = b[this.currentSortKey];
        }
        if (this.currentSortDir === 'desc') {
          modifier = -1;
        }
        if (aVal < bVal) {
          return -1 * modifier;
        }
        if (aVal > bVal) {
          return 1 * modifier;
        }
        return 0;
      });
    },
    filterSearch(data) {
      if (this.search === '') {
        return data;
      }
      return data.filter(
        value => Object.values(value).some((v) => {
          if (typeof v === 'string' && v.toString().toLowerCase().includes(this.search.toLowerCase())) {
            return true;
          }
          return false;
        }),
      );
    },

    sort(s) {
      this.currentSortKey = s;
      this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc';
    },
    deleteRow(row) {
      this.$emit('delete', row);
    },
    editRow(row) {
      this.$emit('edit', row);
    },
    exportList() {
      this.$emit('export');
    },
    addRow() {
      this.$emit('add');
    },
    emitAction(row, action) {
      this.$emit('action', {vm: row, action: action});
    },
    isDescKey(key) {
      return this.currentSortKey === key && this.currentSortDir === 'desc';
    },
    getValue(col, row) {
      return (col.value) ? col.value(row) : '';
    },
    getColor(col, row) {
      return (col.hasOwnProperty('color') && col.color(row)) ? (`is-${col.color(row)}`) : '';
    },
    getIcon(col, row) {
      return (col.hasOwnProperty('icon') && col.icon(row)) ? (`icon icon-${col.icon(row)}`) : '';
    },
    getLink(col, row) {
      return (col.hasOwnProperty('link') && col.link(row)) ? col.link(row) : '';
    },
    getAction(col, row) {
      if (typeof col.action === 'function') {
        col.action(row);
      } else {
        this.$emit(col.action, row);
      }
    },
    hasAction(col, row) {
      return (col.hasOwnProperty('action'));
    },
    getClassList(col, row) {
      const classList = [];

      if (col.classList) {
        classList.push(col.classList);
      }

      if (col.icon) {
        classList.push('has-icon');
      }
      classList.push(this.getColor(col, row));

      return classList.join(' ');
    },

    openSeeMore(row) {
      this.$emit('more', row);
    },

    openActionDropDown(index) {
      this.showStateActionIndex = index === this.showStateActionIndex ?  null : index
    },

    hideStateAction () {
      this.showStateActionIndex = null
    },

    actionVMPermission () {
      if (this.user.is_admin || this.user.roles_obj.some(role => role.action_on_vm)) {
        return true
      }
      return false
    },

    getClassement (classement) {
      if (classement === 'gold') {
        return this.$t('components.table.gold')
      } else if (classement === 'silver') {
        return this.$t('components.table.silver')
      } else if (classement === 'bronze') {
        return 'bronze'
      }
    }

  },
  directives: {
    ClickOutside
  }
};
</script>
